import { useEffect, type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'

import { Skeleton } from '../../ui'
import { useSession } from './useSession'

export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, restore } = useSession()

  useEffect(() => {
    // El access token vive en memoria: tras recargar la página hay que
    // recuperarlo con el refresh token de la cookie.
    if (status === 'idle') void restore()
  }, [status, restore])

  if (status === 'idle' || status === 'loading') {
    /*
     * Esqueleto del armazón, no la palabra «Cargando».
     *
     * Aquí se está resolviendo `/auth/refresh` y `/me`, que desde Lima son dos
     * viajes de ida y vuelta: lo bastante largo para verse. Dibujar ya la
     * barra lateral y la cabecera evita que la pantalla salte cuando llegan,
     * y de paso no promete el tema del estudio antes de conocerlo.
     */
    return (
      <div className="flex min-h-dvh flex-col md:flex-row" aria-label="Abriendo tu cuenta" role="status">
        <div className="hidden border-r border-line bg-surface-raised p-4 md:flex md:w-60 md:flex-col md:gap-2">
          <Skeleton className="h-8 w-36" />
          <div className="mt-5 flex flex-col gap-1.5">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        </div>

        <div className="flex flex-1 flex-col gap-4 px-4 py-5 md:px-8 md:py-7">
          <Skeleton className="h-9 w-48" />
          <Skeleton className="h-32 w-full rounded-card" />
        </div>
      </div>
    )
  }

  if (status === 'anonymous') {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}
